/// <reference path="../../types.d.ts" />
import { state } from "../core/state";
import { NODE_W } from "../core/constants";
import { escapeHtml, formatLifeYears, downloadBlob, wrapSvgTextLines } from "../core/helpers";

export interface PosterLayoutResult {
  nodes: any[];
  marriages: any[];
  brackets: any[];
}

export type LayoutBuilderFn = (rootId: string, ancDepth: number, descDepth: number) => PosterLayoutResult;

export function buildPosterSvgFromConfig(buildHourglassLayout: LayoutBuilderFn): string {
  if (!state.data) return "";
  const scope = (document.getElementById("poster-scope-select") as HTMLSelectElement)?.value || "focused";
  const format = (document.getElementById("poster-format-select") as HTMLSelectElement)?.value || "A1";
  const theme = (document.getElementById("poster-theme-select") as HTMLSelectElement)?.value || "parchment";
  const optRulers = (document.getElementById("poster-opt-rulers") as HTMLInputElement)?.checked ?? true;
  const optBorder = (document.getElementById("poster-opt-border") as HTMLInputElement)?.checked ?? true;
  const optStats = (document.getElementById("poster-opt-stats") as HTMLInputElement)?.checked ?? true;

  const rootId = scope === "all" ? "I227" : state.focusedId;
  const ancDepth = scope === "all" ? 12 : state.ancestorDepth || 5;
  const descDepth = scope === "all" ? 15 : state.descendantDepth || 5;

  const rootPerson = state.data.individuals[rootId] || state.data.individuals["I227"];

  const { nodes, marriages, brackets } = buildHourglassLayout(
    rootId,
    ancDepth,
    descDepth
  );

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const n of nodes) {
    const halfW = (n.width || NODE_W) / 2;
    if (n.x - halfW < minX) minX = n.x - halfW;
    if (n.x + halfW > maxX) maxX = n.x + halfW;
    if (n.y - 44 < minY) minY = n.y - 44;
    if (n.y + 44 > maxY) maxY = n.y + 44;
  }

  if (!isFinite(minX)) {
    minX = -400; maxX = 400; minY = -300; maxY = 300;
  }

  const padX = 140;
  const headerH = 180;
  const footerH = optStats ? 120 : 60;
  const padTop = headerH + 60;
  const padBottom = footerH + 60;

  const naturalW = Math.max(1400, Math.ceil(maxX - minX + padX * 2));
  const naturalH = Math.max(900, Math.ceil(maxY - minY + padTop + padBottom));

  let exportW = naturalW;
  let exportH = naturalH;
  if (format === "A0") {
    exportW = 4480;
    exportH = Math.round((exportW * naturalH) / naturalW);
  } else if (format === "A1") {
    exportW = 3160;
    exportH = Math.round((exportW * naturalH) / naturalW);
  } else if (format === "A2") {
    exportW = 2240;
    exportH = Math.round((exportW * naturalH) / naturalW);
  }

  const offsetX = -minX + padX;
  const offsetY = -minY + padTop;

  let bgCanvas = "#faf7f0";
  let gridDot = "#e3dacb";
  let textPrimary = "#1a2520";
  let textSecondary = "#5c6b64";
  let borderGold = "#c59e4b";
  let cardBg = "#ffffff";
  let cardBorder = "#d1c7b7";
  let linePrimary = "#1a5336";
  let lineSecondary = "#83998e";

  if (theme === "dark") {
    bgCanvas = "#0b1713";
    gridDot = "#192c24";
    textPrimary = "#f3f5f4";
    textSecondary = "#9bb0a6";
    borderGold = "#d4af37";
    cardBg = "#15251f";
    cardBorder = "#2b4539";
    linePrimary = "#10b981";
    lineSecondary = "#4a6b5c";
  } else if (theme === "mono") {
    bgCanvas = "#ffffff";
    gridDot = "#eeeeee";
    textPrimary = "#000000";
    textSecondary = "#444444";
    borderGold = "#333333";
    cardBg = "#ffffff";
    cardBorder = "#888888";
    linePrimary = "#000000";
    lineSecondary = "#666666";
  }

  const svgParts: string[] = [];
  svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${exportW}" height="${exportH}" viewBox="0 0 ${naturalW} ${naturalH}">`);
  svgParts.push(`
    <defs>
      <pattern id="posterGrid" width="30" height="30" patternUnits="userSpaceOnUse">
        <circle cx="15" cy="15" r="1.2" fill="${gridDot}" />
      </pattern>
      <filter id="pCardShadow" x="-10%" y="-10%" width="120%" height="130%">
        <feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="#000000" flood-opacity="0.12" />
      </filter>
    </defs>
  `);

  svgParts.push(`<rect width="${naturalW}" height="${naturalH}" fill="${bgCanvas}" />`);
  svgParts.push(`<rect width="${naturalW}" height="${naturalH}" fill="url(#posterGrid)" />`);

  if (optBorder) {
    svgParts.push(`
      <g>
        <rect x="24" y="24" width="${naturalW - 48}" height="${naturalH - 48}" fill="none" stroke="${borderGold}" stroke-width="4" />
        <rect x="36" y="36" width="${naturalW - 72}" height="${naturalH - 72}" fill="none" stroke="${borderGold}" stroke-width="1.5" opacity="0.6" />
        <circle cx="24" cy="24" r="8" fill="${borderGold}" />
        <circle cx="${naturalW - 24}" cy="24" r="8" fill="${borderGold}" />
        <circle cx="24" cy="${naturalH - 24}" r="8" fill="${borderGold}" />
        <circle cx="${naturalW - 24}" cy="${naturalH - 24}" r="8" fill="${borderGold}" />
      </g>
    `);
  }

  const titleStr = "NİKSİÇ & HOCİÇ SÜLALESİ SOY AĞACI";
  const subStr = scope === "all"
    ? "Karadağ Niksic'ten Türkiye'ye Uzanan 7 Kuşaklık Aile Kökleri (1830 – Günümüz)"
    : `${rootPerson.name} (${formatLifeYears(rootPerson)}) Soyağacı ve Aile Kolları`;

  svgParts.push(`
    <g transform="translate(${naturalW / 2}, 65)" text-anchor="middle">
      <text y="0" font-family="'Segoe UI', sans-serif" font-size="19" fill="${borderGold}" font-weight="700" letter-spacing="4">✦  AİLE TARİHİ VE SOYAĞACI ARŞİVİ  ✦</text>
      <text y="46" font-family="Georgia, 'Times New Roman', serif" font-size="44" font-weight="700" fill="${textPrimary}" letter-spacing="1.5">${escapeHtml(titleStr)}</text>
      <text y="82" font-family="'Segoe UI', sans-serif" font-size="18" font-weight="600" fill="${textSecondary}">${escapeHtml(subStr)}</text>
      <line x1="-360" y1="108" x2="360" y2="108" stroke="${borderGold}" stroke-width="2" />
      <polygon points="0,103 6,108 0,113 -6,108" fill="${borderGold}" />
    </g>
  `);

  if (optRulers) {
    const minGenY = minY + offsetY;
    const maxGenY = maxY + offsetY;
    const genNames = ["1. Kuşak (~1830)", "2. Kuşak (~1860)", "3. Kuşak (~1890)", "4. Kuşak (~1925)", "5. Kuşak (~1955)", "6. Kuşak (~1985)", "7. Kuşak (2015+)"];
    const stepY = (maxGenY - minGenY) / Math.max(1, genNames.length - 1);

    svgParts.push(`<g font-family="'Segoe UI', sans-serif" font-size="11" font-weight="700" fill="${borderGold}">`);
    genNames.forEach((gName, idx) => {
      const gy = minGenY + idx * stepY;
      svgParts.push(`<text x="50" y="${gy + 4}" text-anchor="start">${escapeHtml(gName)}</text>`);
      svgParts.push(`<line x1="125" y1="${gy}" x2="${naturalW - 125}" y2="${gy}" stroke="${gridDot}" stroke-dasharray="4 6" stroke-width="1" />`);
      svgParts.push(`<text x="${naturalW - 50}" y="${gy + 4}" text-anchor="end">${escapeHtml(gName)}</text>`);
    });
    svgParts.push(`</g>`);
  }

  for (const m of marriages) {
    const x1 = m.x1 + offsetX;
    const x2 = m.x2 + offsetX;
    const y = m.y + offsetY;
    svgParts.push(`<path d="M ${x1} ${y} L ${x2} ${y}" fill="none" stroke="#16a34a" stroke-width="3" stroke-linecap="round" />`);
  }

  for (const b of brackets) {
    if (!b.children || b.children.length === 0) continue;
    const px = b.parentX + offsetX;
    const py = b.parentY + offsetY;
    const cy = b.childY + offsetY;
    const midY = b.midY + offsetY;
    const childXs = b.children.map((c: any) => c.x + offsetX);
    const barMinX = Math.min(px, ...childXs);
    const barMaxX = Math.max(px, ...childXs);
    const baseStroke = b.isDescendant ? linePrimary : lineSecondary;
    const baseWidth = b.isDescendant ? "2.6" : "2";

    svgParts.push(`<path d="M ${px} ${py} V ${midY} M ${barMinX} ${midY} H ${barMaxX}" fill="none" stroke="${baseStroke}" stroke-width="${baseWidth}" stroke-linecap="round" stroke-linejoin="round" />`);
    for (const ch of b.children) {
      const cx = ch.x + offsetX;
      svgParts.push(`<path d="M ${cx} ${midY} V ${cy}" fill="none" stroke="${baseStroke}" stroke-width="${baseWidth}" stroke-linecap="round" />`);
    }
  }

  const cardH = 82;
  for (const n of nodes) {
    const cardW = n.width || NODE_W;
    const cx = n.x + offsetX;
    const cy = n.y + offsetY;
    const x = cx - cardW / 2;
    const y = cy - cardH / 2;
    const p = n.person;
    const isFemale = p.sex === "F";
    const accentColor = isFemale ? "#c02669" : "#2563eb";
    const borderStroke = n.isFocused ? borderGold : cardBorder;
    const borderWidth = n.isFocused ? "2.8" : "1.4";

    svgParts.push(`<g transform="translate(${x.toFixed(1)}, ${y.toFixed(1)})">`);
    svgParts.push(`<rect width="${cardW}" height="${cardH}" rx="10" fill="${cardBg}" stroke="${borderStroke}" stroke-width="${borderWidth}" filter="url(#pCardShadow)" />`);
    svgParts.push(`<path d="M 8 0 H 3.5 A 3.5 3.5 0 0 0 0 3.5 V ${cardH - 3.5} A 3.5 3.5 0 0 0 3.5 ${cardH} H 8 Z" fill="${n.isFocused ? borderGold : accentColor}" />`);

    const nameLines = wrapSvgTextLines(p.given || p.name, 17);
    const surname = (p.surname || "").trim();
    const lifeText = formatLifeYears(p);

    svgParts.push(`<g transform="translate(18, 22)">`);
    nameLines.forEach((nl, i) => {
      svgParts.push(`<text x="0" y="${i * 15}" font-family="'Segoe UI', Roboto, sans-serif" font-size="12.5" font-weight="700" fill="${textPrimary}">${escapeHtml(nl)}</text>`);
    });
    if (surname) {
      svgParts.push(`<text x="0" y="${nameLines.length * 15 + 1}" font-family="'Segoe UI', sans-serif" font-size="10" font-weight="600" fill="${textSecondary}">${escapeHtml(surname)}</text>`);
    }
    svgParts.push(`<text x="0" y="${cardH - 34}" font-family="'Segoe UI', monospace" font-size="9.5" font-weight="600" fill="${textSecondary}">${escapeHtml(lifeText)}</text>`);
    svgParts.push(`</g>`);
    svgParts.push(`</g>`);
  }

  if (optStats) {
    const countTotal = Object.keys(state.data.individuals).length;
    const countFams = Object.keys(state.data.families).length;
    const dateStr = new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
    svgParts.push(`
      <g transform="translate(${naturalW / 2}, ${naturalH - 55})" text-anchor="middle" font-family="'Segoe UI', sans-serif">
        <line x1="-400" y1="-25" x2="400" y2="-25" stroke="${borderGold}" stroke-width="1.5" />
        <polygon points="0,-30 5,-25 0,-20 -5,-25" fill="${borderGold}" />
        <text y="0" font-size="14" font-weight="700" fill="${textPrimary}">Toplam ${countTotal} Aile Üyesi  •  ${countFams} Aile Bağı  •  7 Kuşak  •  Baskı Tarihi: ${escapeHtml(dateStr)}</text>
        <text y="22" font-size="12" font-weight="600" fill="${borderGold}">🌐 nh.ecemaya.com  •  Resmi Niksiç &amp; Hociç Sülalesi Portalı</text>
      </g>
    `);
  }

  svgParts.push(`</svg>`);
  return svgParts.join("\n");
}

export function openPosterExportModal(options: {
  buildHourglassLayout: LayoutBuilderFn;
  openModal: (title: string, htmlContent: string, isWide?: boolean) => void;
  closeModal: () => void;
}): void {
  if (!state.data) return;
  const focusedPerson = state.data.individuals[state.focusedId] || state.data.individuals["I227"];

  const modalHtml = `
    <div style="display: flex; flex-direction: column; gap: 1rem;">
      <p class="section-subtitle">
        ${state.lang === "tr" ? "Matbaa ve ev tipi yazıcılar için ultra yüksek çözünürlüklü A0, A1, A2 dev duvar posteri ve vektörel SVG çıktısı oluşturun." : "Generate high-resolution wall posters and vector SVG prints for printers."}
      </p>

      <div class="poster-config-grid">
        <div class="poster-option-group">
          <label for="poster-scope-select">🌳 ${state.lang === "tr" ? "Soyağacı Kapsamı" : "Tree Scope"}</label>
          <select id="poster-scope-select" class="form-input">
            <option value="all">👑 ${state.lang === "tr" ? "Tüm Sülale Ağacı (778 Kişi - Holoviç Molla Ahmet)" : "Whole Tree (778 Individuals)"}</option>
            <option value="focused" selected>🎯 ${state.lang === "tr" ? "Mevcut Odak Kişi ve Kolu" : "Focused Branch"} (${escapeHtml(focusedPerson ? focusedPerson.name : "Odak")})</option>
          </select>
        </div>

        <div class="poster-option-group">
          <label for="poster-format-select">📐 ${state.lang === "tr" ? "Kağıt Boyutu / Format" : "Paper Size"}</label>
          <select id="poster-format-select" class="form-input">
            <option value="A0">🏆 A0 Dev Duvar Posteri (841 x 1189 mm)</option>
            <option value="A1" selected>🖼️ A1 Sergi Posteri (594 x 841 mm)</option>
            <option value="A2">📄 A2 Standart Poster (420 x 594 mm)</option>
            <option value="SVG">⚡ Kayıpsız Vektörel SVG (Sonsuz Büyütme)</option>
          </select>
        </div>

        <div class="poster-option-group">
          <label for="poster-theme-select">🎨 ${state.lang === "tr" ? "Baskı Teması" : "Print Theme"}</label>
          <select id="poster-theme-select" class="form-input">
            <option value="parchment" selected>📜 ${state.lang === "tr" ? "Klasik Parşömen & Altın (Baskı İçin İdeal)" : "Classic Parchment & Gold"}</option>
            <option value="dark">🌲 ${state.lang === "tr" ? "Zümrüt Yeşili & Lüks Koyu Tema" : "Emerald Luxury Dark"}</option>
            <option value="mono">🖨️ ${state.lang === "tr" ? "Yüksek Kontrast Siyah-Beyaz (Ekonomik)" : "Monochrome High Contrast"}</option>
          </select>
        </div>
      </div>

      <div style="background: var(--bg-surface); padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid var(--border-subtle); display: flex; flex-direction: column; gap: 0.5rem; font-size: 0.85rem;">
        <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
          <input type="checkbox" id="poster-opt-rulers" checked>
          <span>📏 ${state.lang === "tr" ? "Kuşak ve Zaman Cetvellerini Kenarlara Ekle (1. Kuşak ~1830 ... 7. Kuşak 2026)" : "Include Generation Timeline Rulers"}</span>
        </label>
        <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
          <input type="checkbox" id="poster-opt-border" checked>
          <span>⚜️ ${state.lang === "tr" ? "Dekoratif Osmanlı / Balkan Miras Çerçevesi ve Sülale Arması" : "Ornate Heritage Frame and Crest"}</span>
        </label>
        <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
          <input type="checkbox" id="poster-opt-stats" checked>
          <span>📊 ${state.lang === "tr" ? "Alt Bilgi İstatistikleri (Toplam Kişi Sayısı, Kuşaklar, Tarih)" : "Include Footer Statistics Bar"}</span>
        </label>
      </div>

      <div style="display: flex; gap: 0.6rem; justify-content: flex-end; margin-top: 0.5rem;">
        <button type="button" class="mini-btn" id="close-poster-modal-btn">✕ ${state.lang === "tr" ? "Kapat" : "Close"}</button>
        <button type="button" class="primary-btn" id="do-export-svg-btn" style="padding: 0.75rem 1.25rem;">
          📥 ${state.lang === "tr" ? "Vektörel SVG İndir" : "Download SVG"}
        </button>
        <button type="button" class="primary-btn" id="do-print-poster-btn" style="padding: 0.75rem 1.25rem; background: var(--accent-gold); border-color: var(--accent-gold); color: #000; font-weight: 700;">
          🖨️ ${state.lang === "tr" ? "Baskı Önizlemesi & Yazdır (PDF)" : "Print Preview (PDF)"}
        </button>
      </div>
    </div>
  `;

  options.openModal(`🖨️ ${state.lang === "tr" ? "Dev Aile Posteri & Vektörel Baskı" : "Poster & Vector Print"}`, modalHtml, true);

  document.getElementById("close-poster-modal-btn")?.addEventListener("click", options.closeModal);

  document.getElementById("do-export-svg-btn")?.addEventListener("click", () => {
    const svgStr = buildPosterSvgFromConfig(options.buildHourglassLayout);
    const format = (document.getElementById("poster-format-select") as HTMLSelectElement).value;
    const scope = (document.getElementById("poster-scope-select") as HTMLSelectElement).value;
    const filename = `Niksic_Hocic_Soyagaci_${scope === "all" ? "Tum_Sulale" : (focusedPerson.name || "Odak").replace(/\s+/g, "_")}_${format}.svg`;
    downloadBlob(svgStr, filename, "image/svg+xml;charset=utf-8");
    options.closeModal();
  });

  document.getElementById("do-print-poster-btn")?.addEventListener("click", () => {
    const svgStr = buildPosterSvgFromConfig(options.buildHourglassLayout);
    const printWin = window.open("", "_blank");
    if (!printWin) {
      alert("Lütfen açılır pencerelere izin verin.");
      return;
    }
    printWin.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Niksiç & Hociç Soy Ağacı Posteri</title>
          <style>
            @page { size: auto; margin: 0; }
            body { margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; background: #fff; }
            svg { width: 100vw; height: auto; max-height: 100vh; }
          </style>
        </head>
        <body>
          ${svgStr}
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 400);
            };
          </script>
        </body>
      </html>
    `);
    printWin.document.close();
    options.closeModal();
  });
}
